import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import { GastoPlan, PagoPlan, Plan } from '../types';
import {
  calcularSaldos, simplificarDeudas, resumenPorParticipante, totalPorCategoria, esCero, formatoEuros,
} from './amediasUtils';
import { NOMBRE_APP } from '../config/marca';
import { guardarPDF } from './plataforma';

/**
 * PDF de un plan de A medias, para compartirlo o guardarlo al cerrar cuentas:
 * resumen, quién debe a quién, gastos y pagos.
 *
 * Las fuentes estándar de jsPDF solo cubren Latin-1 (más el €): nada de
 * emojis, flechas ni el signo menos tipográfico. Por eso el icono del plan no
 * sale y los negativos llevan guion.
 */
const VERDE: [number, number, number] = [5, 150, 105];
const GRIS: [number, number, number] = [107, 114, 128];

const euros = (e: number) => formatoEuros(e).replace('−', '-');
const conSigno = (e: number) => (esCero(e) ? euros(0) : `${e > 0 ? '+' : '-'}${euros(Math.abs(e))}`);
const fecha = (d: Date) => format(d, 'dd/MM/yyyy');

export function exportarPlanPDF(plan: Plan, gastos: GastoPlan[], pagos: PagoPlan[]): void {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const nombre = (id: string) => plan.participantes.find(p => p.id === id)?.nombre ?? '(ya no está)';
  const saldos = calcularSaldos(gastos, pagos);
  const deudas = simplificarDeudas(saldos);
  const resumen = resumenPorParticipante(gastos, pagos, plan.participantes.map(p => p.id));
  const porCategoria = totalPorCategoria(gastos);
  const total = gastos.reduce((s, g) => s + g.importe, 0);

  // ── Cabecera ──
  doc.setFillColor(...VERDE);
  doc.rect(0, 0, 210, 32, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(plan.nombre, 14, 14);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const fechas = [plan.fechaInicio && fecha(plan.fechaInicio), plan.fechaFin && fecha(plan.fechaFin)].filter(Boolean).join(' - ');
  doc.text([fechas, `${plan.participantes.length} personas`].filter(Boolean).join('  ·  '), 14, 22);
  doc.text(`A medias · generado el ${fecha(new Date())}`, 196, 22, { align: 'right' });

  // ── Cifras ──
  doc.setTextColor(17, 24, 39);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(`Gasto total: ${euros(total)}`, 14, 42);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRIS);
  doc.text(`${gastos.length} gastos · ${pagos.length} pagos`, 14, 48);

  const titulo = (texto: string, y: number) => {
    doc.setTextColor(17, 24, 39);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(texto, 14, y);
    return y + 3;
  };
  const siguienteY = (margen = 10) => ((doc as any).lastAutoTable?.finalY ?? 50) + margen;
  const estiloTabla = {
    headStyles: { fillColor: VERDE, textColor: 255, fontStyle: 'bold' as const, fontSize: 9 },
    bodyStyles: { fontSize: 9 },
    alternateRowStyles: { fillColor: [240, 253, 244] as [number, number, number] },
    margin: { left: 14, right: 14 },
  };

  // ── Quién debe a quién ──
  let y = titulo('Quién debe a quién', 58);
  if (deudas.length === 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...VERDE);
    doc.text('Todo el mundo está al día.', 14, y + 5);
    y += 12;
  } else {
    autoTable(doc, {
      ...estiloTabla,
      startY: y,
      head: [['Debe', 'A', 'Importe']],
      body: deudas.map(d => [nombre(d.de), nombre(d.a), euros(d.importe)]),
      columnStyles: { 2: { halign: 'right', fontStyle: 'bold' } },
    });
    y = siguienteY();
  }

  // ── Por persona ──
  y = titulo('Por persona', y);
  autoTable(doc, {
    ...estiloTabla,
    startY: y,
    head: [['Persona', 'Ha pagado', 'Su parte', 'Pagos (hechos - recibidos)', 'Saldo']],
    body: plan.participantes.map(p => {
      const r = resumen[p.id];
      const pagosNetos = r.pagosHechos - r.pagosRecibidos;
      return [p.nombre, euros(r.pagado), euros(r.consumido), esCero(pagosNetos) ? '—' : conSigno(pagosNetos), conSigno(r.saldo)];
    }),
    columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right', fontStyle: 'bold' } },
  });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...GRIS);
  doc.text('Saldo positivo: le deben. Negativo: debe.', 14, siguienteY(4));

  // ── Por categoría ──
  if (porCategoria.length > 1) {
    y = titulo('Por categoría', siguienteY(12));
    autoTable(doc, {
      ...estiloTabla,
      startY: y,
      head: [['Categoría', 'Importe', '%']],
      body: porCategoria.map(c => [c.categoria, euros(c.importe), `${Math.round((c.importe / total) * 100)} %`]),
      columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' } },
    });
  }

  // ── Gastos ──
  y = titulo('Gastos', siguienteY(12));
  autoTable(doc, {
    ...estiloTabla,
    startY: y,
    head: [['Fecha', 'Concepto', 'Pagó', 'Entre', 'Importe']],
    body: [...gastos].sort((a, b) => a.fecha.getTime() - b.fecha.getTime()).map(g => [
      fecha(g.fecha),
      g.categoria ? `${g.concepto} (${g.categoria})` : g.concepto,
      Object.keys(g.pagadoPor).map(nombre).join(', '),
      Object.keys(g.importes).map(nombre).join(', '),
      euros(g.importe),
    ]),
    columnStyles: { 0: { cellWidth: 22 }, 4: { halign: 'right', cellWidth: 24 } },
  });

  // ── Pagos ──
  if (pagos.length > 0) {
    y = titulo('Pagos entre participantes', siguienteY(12));
    autoTable(doc, {
      ...estiloTabla,
      startY: y,
      head: [['Fecha', 'Paga', 'Recibe', 'Nota', 'Importe']],
      body: [...pagos].sort((a, b) => a.fecha.getTime() - b.fecha.getTime()).map(p => [
        fecha(p.fecha), nombre(p.de), nombre(p.a), p.nota ?? '', euros(p.importe),
      ]),
      columnStyles: { 0: { cellWidth: 22 }, 4: { halign: 'right', cellWidth: 24 } },
    });
  }

  // ── Pie con número de página ──
  const paginas = doc.getNumberOfPages();
  for (let i = 1; i <= paginas; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...GRIS);
    doc.text(`${NOMBRE_APP} · A medias · ${plan.nombre}`, 14, 290);
    doc.text(`${i} / ${paginas}`, 196, 290, { align: 'right' });
  }

  const archivo = plan.nombre.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^\w]+/g, '_').toLowerCase();
  guardarPDF(doc, `a_medias_${archivo || 'plan'}.pdf`);
}
